import type { RunResponse } from '@malaberg/shared';
/**
 * use-run-stream — T24.
 *
 * Manages the EventSource lifecycle for a single run.
 * Receives a runId (or null when no active run).
 *
 * On runId change:
 *   - Previous SSE connection is closed.
 *   - State is reset to idle.
 *   - New connection opened when runId is non-null.
 *
 * Reconnect: if runId doesn't change but no event is received for 30s,
 * the consumer closes and reopens after 5s.
 */
import { useEffect, useRef } from 'react';
import { apiClient } from '../api/client';
import type { SSEHandle } from '../api/client';
import { useRunStreamStore } from '../state/run-stream-store';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface RunStreamResult {
  progress: Array<{ phase: string; message: string; at: string }>;
  deltaText: string;
  status: 'idle' | 'streaming' | 'done' | 'error';
  error: string | null;
  runResult: RunResponse | null;
  close: () => void;
}

// ─── Error code → user-facing message map (PRD K-table) ──────────────────────

const ERROR_CODE_MAP: Record<string, string> = {
  generation_failed:
    'Generation failed. Queued ads retry automatically; failed queue items can be requeued from Queue.',
  output_visibility_leak:
    'Generation failed. Queued ads retry automatically; failed queue items can be requeued from Queue.',
  service_misconfigured: 'Service is misconfigured. Please contact an administrator.',
  aborted_by_restart: 'Generation interrupted by server restart. Please try again.',
};

function mapErrorCode(code: string, serverMessage: string): string {
  return ERROR_CODE_MAP[code] ?? serverMessage;
}

// ─── Cost formatter (exported for unit tests) ─────────────────────────────────

/**
 * Formats an estimated cost in USD for display. Always 2 decimal places,
 * regardless of role.
 *
 * The `role` argument is preserved for backward call-site compatibility but
 * is no longer consulted.
 */
export function formatCost(
  cost: number | null,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _role?: 'writer' | 'admin' | 'system_owner'
): string | null {
  if (cost === null) return null;
  return `$${cost.toFixed(2)}`;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Opens and manages an SSE connection for the given runId.
 * When runId is null, returns idle state without connecting.
 */
export function useRunStream(runId: string | null): RunStreamResult {
  const store = useRunStreamStore();
  const handleRef = useRef<SSEHandle | null>(null);
  // Tracks the last-received-event timestamp for reconnect logic
  const lastEventRef = useRef<number>(Date.now());
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const deferredCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Exposed close function — callers can abort the stream early
  function close() {
    handleRef.current?.close();
    handleRef.current = null;
    clearTimers();
    clearDeferredCloseTimer();
  }

  function clearTimers() {
    if (reconnectTimerRef.current !== null) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    if (idleTimerRef.current !== null) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
  }

  function clearDeferredCloseTimer() {
    if (deferredCloseTimerRef.current !== null) {
      clearTimeout(deferredCloseTimerRef.current);
      deferredCloseTimerRef.current = null;
    }
  }

  function openConnection(id: string) {
    lastEventRef.current = Date.now();

    const handle = apiClient.openSSE(`/api/runs/${id}/events`, {
      onEvent({ event, data }) {
        // Reset the idle timer on every event
        lastEventRef.current = Date.now();
        resetIdleTimer(id);

        try {
          const payload = JSON.parse(data) as Record<string, unknown>;

          switch (event) {
            case 'progress':
              store.appendProgress({
                phase: String(payload.phase ?? ''),
                message: String(payload.message ?? ''),
                at: String(payload.at ?? ''),
              });
              break;

            case 'delta':
              store.appendDelta(String(payload.delta ?? ''));
              break;

            case 'done':
              clearTimers();
              store.setDone(payload.run as RunResponse);
              break;

            case 'error': {
              clearTimers();
              const code = String(payload.code ?? '');
              const message = String(payload.error ?? 'An unknown error occurred.');
              store.setError(mapErrorCode(code, message), {
                clearDeltaText: code === 'output_visibility_leak',
              });
              break;
            }

            default:
              // Heartbeat pings arrive as comment lines and are handled by the
              // SSE parser — they don't reach onEvent. Unknown named events are
              // silently ignored.
              break;
          }
        } catch {
          // Malformed JSON — ignore silently
        }
      },

      onError(_err) {
        // Network drop — the reconnect logic will reopen after 5s
        // Only set error state if we're not going to reconnect
        // (idle timer will handle reconnect; terminal errors are handled by 'error' event)
      },
    });

    handleRef.current = handle;
    resetIdleTimer(id);
  }

  /**
   * Starts a 30s idle timer. If no event is received in that window, closes
   * the connection and reopens it after 5s.
   */
  function resetIdleTimer(id: string) {
    if (idleTimerRef.current !== null) {
      clearTimeout(idleTimerRef.current);
    }

    idleTimerRef.current = setTimeout(() => {
      const timeSinceLastEvent = Date.now() - lastEventRef.current;
      if (timeSinceLastEvent >= 30_000) {
        // No events for 30s — close and schedule reconnect in 5s
        handleRef.current?.close();
        handleRef.current = null;

        reconnectTimerRef.current = setTimeout(() => {
          openConnection(id);
        }, 5_000);
      }
    }, 30_000);
  }

  // Track the currently-active runId across StrictMode re-mounts so the
  // double-invocation cleanup cycle doesn't abort an in-flight stream that
  // the second mount immediately re-establishes.
  const activeRunIdRef = useRef<string | null>(null);

  useEffect(() => {
    clearDeferredCloseTimer();

    if (!runId) {
      // No active run — close any existing connection.
      activeRunIdRef.current = null;
      close();
      return;
    }

    // If the SAME runId is already streaming (from a prior mount in the
    // StrictMode pair), reuse the existing connection.
    if (activeRunIdRef.current === runId && handleRef.current) {
      return;
    }

    // New runId — close any prior connection (different run), then open.
    close();
    activeRunIdRef.current = runId;
    // Only reset state when transitioning from one DIFFERENT run to another
    // (e.g. clicking a different run card). If status is already 'streaming'
    // it's because the user just clicked Generate — keep the loading state
    // intact, otherwise we flash the empty placeholder before the first
    // SSE event lands.
    if (store.status !== 'streaming') {
      store.reset();
    }
    openConnection(runId);

    return () => {
      // Defer the close. A subsequent mount with the same runId will see
      // `activeRunIdRef.current === runId` and short-circuit, leaving the
      // stream intact.
      const capturedRunId = activeRunIdRef.current;
      clearDeferredCloseTimer();
      deferredCloseTimerRef.current = setTimeout(() => {
        deferredCloseTimerRef.current = null;
        if (activeRunIdRef.current === capturedRunId) {
          // No re-mount happened: actually clean up now.
          close();
          activeRunIdRef.current = null;
        }
      }, 100);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId]);

  return {
    progress: store.progress,
    deltaText: store.deltaText,
    status: store.status,
    error: store.error,
    runResult: store.runResult,
    close,
  };
}
